/**
 * Types for tenant-facing org / invitation endpoints on platform-api
 * (PR-E.1 scope — N:1 account ↔ org).
 *
 * Realm split:
 *   - `/v1/orgs/:orgId/...`                — session-gated, owner / admin
 *                                            only. Members listing + invite
 *                                            lifecycle (create / list /
 *                                            revoke).
 *   - `/v1/invitations/:token/...`         — token-gated; anyone with a
 *                                            valid plaintext invitation
 *                                            token can read the summary and
 *                                            accept. The token itself is
 *                                            the authorization.
 *
 * Role enum: `{owner, admin, member, finance}`. "owner" is reserved — only
 * the original registrant of the org holds it, and it's never issued via
 * invitation. Invitations and `POST /v1/orgs/.../invitations` both reject
 * `role: 'owner'`. Custom roles (tenant #178) hang off `roleId` and keep
 * this builtin set for the fallback. `finance` is a builtin with a closed
 * read/export permission set; it is not editable.
 */

import type { TenantOrgPermission } from './public-auth.ts';

export type OrgMemberRole = 'owner' | 'admin' | 'member' | 'finance';
export type OrgInvitationRole = 'admin' | 'member' | 'finance';
export type OrgMemberStatus = 'active' | 'disabled' | 'deleted';
export type OrgModelPermissionMode = 'inherit' | 'all' | 'custom';
export type OrgModelPermissionSource = 'member' | 'org_unit' | 'global';
export const BUILTIN_ORG_ROLE_IDS = ['owner', 'admin', 'member', 'finance'] as const;
export type BuiltinOrgRoleId = (typeof BUILTIN_ORG_ROLE_IDS)[number];

/**
 * Member / invitation assignment: a builtin name (`admin` / `member` /
 * `finance`; `owner` is never assigned this way) **or** a custom
 * `org_roles.id`. Exactly one of `role` / `roleId` is set on write.
 */
export interface OrgMemberRoleAssignment {
  role?: Exclude<OrgMemberRole, 'owner'>;
  roleId?: string;
}

/**
 * Snapshot of an org member (owner, admin, member, or finance). Consumed
 * by the Settings → Members table on console-web.
 */
export interface OrgMemberSummary {
  accountId: string;
  email: string;
  role: OrgMemberRole;
  /** Custom org role id when assigned; `null` for builtin owner/admin/member/finance. */
  customRoleId: string | null;
  displayName: string | null;
  department: string | null;
  orgUnitId: string | null;
  orgUnitName: string | null;
  orgUnitPath: string | null;
  status: OrgMemberStatus;
  note: string | null;
  monthlyLimitUsdCents: number | null;
  modelPermissionMode: OrgModelPermissionMode;
  joinedAt: string;
}

export interface ListOrgMembersResponse {
  members: readonly OrgMemberSummary[];
}

export interface UpdateOrgMemberRequest {
  displayName?: string | null;
  department?: string | null;
  primaryOrgUnitId?: string | null;
  status?: OrgMemberStatus;
  note?: string | null;
  /**
   * Builtin assignment (`admin` / `member` / `finance`). Mutually exclusive
   * with {@link UpdateOrgMemberRequest.roleId}. `owner` is refused.
   */
  role?: Exclude<OrgMemberRole, 'owner'>;
  /**
   * Custom `org_roles.id`. Mutually exclusive with
   * {@link UpdateOrgMemberRequest.role}.
   */
  roleId?: string;
  /**
   * Positive integer USD cents, or `null` for no ceiling. `0` is refused with
   * 400 (#1620) — disable the membership to stop it spending.
   */
  monthlyLimitUsdCents?: number | null;
}

export interface UpdateOrgMemberResponse {
  member: OrgMemberSummary;
  cache: {
    revision: string | null;
    convergence: 'immediate' | 'degraded';
    effective_after: string;
  };
}

export type OrgUnitStatus = 'active' | 'archived';

export interface OrgUnitSummary {
  id: string;
  name: string;
  path: string;
  parentId: string | null;
  status: OrgUnitStatus;
  memberCount: number;
  modelPermissionMode: OrgModelPermissionMode;
  isDefaultRoot: boolean;
}

export interface CreateOrgUnitRequest {
  name: string;
  parentId?: string | null;
}

export interface CreateOrgUnitResponse {
  unit: OrgUnitSummary;
}

export interface UpdateOrgUnitRequest {
  name?: string;
  parentId?: string | null;
}

export interface UpdateOrgUnitResponse {
  unit: OrgUnitSummary;
  cache: {
    revision: string | null;
    convergence: 'immediate' | 'degraded';
    effective_after: string;
  };
}

export interface ArchiveOrgUnitResponse {
  unit: OrgUnitSummary;
}

export interface ListOrgUnitsResponse {
  units: readonly OrgUnitSummary[];
  summary: {
    totalUnits: number;
    unassignedMembers: number;
  };
}

export interface OrgMemberModelPermissionSummary {
  /** Namespace derived from the authoritative SKU. */
  namespaceId: string;
  vendor: string;
  model: string;
  isDefault: boolean;
  /** Authoritative SKU identity of the grant. */
  skuId: string;
  /** Canonical `namespace/model` name of the SKU. */
  skuName: string;
}

/**
 * SKU-keyed permission write entry. Platform stores only the SKU id and derives
 * namespace/vendor/model display metadata from the marketplace catalog.
 */
export interface OrgSkuModelPermissionWriteEntry {
  skuId: string;
  isDefault?: boolean;
}

export type OrgModelPermissionWriteEntry = OrgSkuModelPermissionWriteEntry;

export interface OrgModelPermissionInheritanceSummary {
  source: OrgModelPermissionSource;
  mode: Exclude<OrgModelPermissionMode, 'inherit'>;
  orgUnitId: string | null;
  orgUnitName: string | null;
  orgUnitPath: string | null;
}

export interface ListOrgMemberModelPermissionsResponse {
  mode: OrgModelPermissionMode;
  effectiveMode: Exclude<OrgModelPermissionMode, 'inherit'>;
  inheritedFrom: OrgModelPermissionInheritanceSummary | null;
  permissions: readonly OrgMemberModelPermissionSummary[];
  effectivePermissions: readonly OrgMemberModelPermissionSummary[];
}

export interface ReplaceOrgMemberModelPermissionsRequest {
  mode?: OrgModelPermissionMode;
  permissions?: readonly OrgModelPermissionWriteEntry[];
}

export interface ReplaceOrgMemberModelPermissionsResponse {
  mode: OrgModelPermissionMode;
  effectiveMode: Exclude<OrgModelPermissionMode, 'inherit'>;
  inheritedFrom: OrgModelPermissionInheritanceSummary | null;
  permissions: readonly OrgMemberModelPermissionSummary[];
  effectivePermissions: readonly OrgMemberModelPermissionSummary[];
  cache: {
    revision: string | null;
    convergence: 'immediate' | 'degraded';
    effective_after: string;
  };
}

export type OrgUnitModelPermissionSummary = OrgMemberModelPermissionSummary;

export interface ListOrgUnitModelPermissionsResponse {
  mode: OrgModelPermissionMode;
  effectiveMode: Exclude<OrgModelPermissionMode, 'inherit'>;
  inheritedFrom: OrgModelPermissionInheritanceSummary | null;
  permissions: readonly OrgUnitModelPermissionSummary[];
  effectivePermissions: readonly OrgUnitModelPermissionSummary[];
}

export interface ReplaceOrgUnitModelPermissionsRequest {
  mode?: OrgModelPermissionMode;
  permissions?: readonly OrgModelPermissionWriteEntry[];
}

export interface ReplaceOrgUnitModelPermissionsResponse {
  mode: OrgModelPermissionMode;
  effectiveMode: Exclude<OrgModelPermissionMode, 'inherit'>;
  inheritedFrom: OrgModelPermissionInheritanceSummary | null;
  permissions: readonly OrgUnitModelPermissionSummary[];
  effectivePermissions: readonly OrgUnitModelPermissionSummary[];
  cache: {
    revision: string | null;
    convergence: 'immediate' | 'degraded';
    effective_after: string;
  };
}

export interface TenantAuditEventSummary {
  id: string;
  orgId: string;
  actorAccountId: string | null;
  targetAccountId: string | null;
  action: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface ListTenantAuditEventsResponse {
  events: readonly TenantAuditEventSummary[];
  limit: number;
  offset: number;
}

/**
 * Pending invitation snapshot. Includes `token` = null always — plaintext
 * tokens are never round-tripped back to the listing caller, only to the
 * creator (once, in `CreateOrgInvitationResponse.inviteUrl`).
 */
export interface OrgInvitationSummary {
  id: string;
  orgId: string;
  email: string;
  role: OrgInvitationRole;
  /** Custom org role id when the invite hangs on a custom row; else `null`. */
  customRoleId: string | null;
  invitedByAccountId: string;
  invitedByEmail: string | null;
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
}

export interface ListOrgInvitationsResponse {
  invitations: readonly OrgInvitationSummary[];
}

export interface CreateOrgInvitationRequest {
  email: string;
  /**
   * Builtin invitation role. Required unless {@link CreateOrgInvitationRequest.roleId}
   * is set; the two are mutually exclusive. `owner` is never issued.
   */
  role?: OrgInvitationRole;
  /** Custom `org_roles.id` in this org. Mutually exclusive with `role`. */
  roleId?: string;
}

/**
 * Response to `POST /v1/orgs/:orgId/invitations`. The `inviteUrl` carries
 * the plaintext token once — the caller is expected to forward it via
 * email (which the server also does server-side), or display it for the
 * owner to share manually if email delivery is unavailable.
 */
export interface CreateOrgInvitationResponse {
  invitation: OrgInvitationSummary;
  inviteUrl: string;
}

export interface RevokeOrgInvitationResponse {
  ok: true;
  id: string;
  revokedAt: string;
}

/**
 * Unauthenticated read — any caller with the plaintext token can look up
 * the org name + invited role so the console can render a meaningful
 * accept page ("Join <Org> as <role>").
 *
 * Does NOT leak the inviter's email beyond masking rules applied by
 * `invitedByEmail` (today: returned verbatim; future: consider redacting).
 */
export interface GetOrgInvitationResponse {
  invitationId: string;
  orgId: string;
  orgName: string;
  role: OrgInvitationRole;
  customRoleId: string | null;
  email: string;
  invitedByEmail: string | null;
  expiresAt: string;
}

/**
 * Accept-flow request. Three cases the server handles:
 *   1. No cookie + unknown email → `password` (and optional `displayName`)
 *      required. Creates a new account, joins the invited org, returns a
 *      fresh session cookie via `Set-Cookie`.
 *   2. No cookie + known email → `password` required; verifies and joins
 *      the logged-out account to the org, issues a fresh session cookie.
 *   3. Active session → cookie identity is used verbatim; `password`
 *      is ignored if supplied. Account joins the invited org under its
 *      existing identity (no new account created).
 */
export interface AcceptOrgInvitationRequest {
  password?: string;
  displayName?: string;
}

export interface AcceptOrgInvitationResponse {
  accountId: string;
  orgId: string;
  /** True when a brand-new account was created by the accept flow. */
  createdAccount: boolean;
}

/**
 * One row of `GET /v1/org/roles` — builtins (from code constants) plus
 * custom `org_roles` rows. The matrix page's single source of truth.
 */
export interface OrgRoleView {
  id: string;
  name: string;
  builtin: boolean;
  permissions: readonly TenantOrgPermission[];
  memberCount: number;
}

export interface ListOrgRolesResponse {
  roles: readonly OrgRoleView[];
}

export interface CreateOrgRoleRequest {
  name: string;
  permissions: readonly TenantOrgPermission[];
}

export interface CreateOrgRoleResponse {
  role: OrgRoleView;
}

export interface UpdateOrgRoleRequest {
  name?: string;
  permissions?: readonly TenantOrgPermission[];
}

export interface UpdateOrgRoleResponse {
  role: OrgRoleView;
}

export interface DeleteOrgRoleResponse {
  ok: true;
}

/**
 * Client-reported export audit event (`POST /v1/org/audit/events`).
 * Stored with an explicit client-reported label; not a server-side export.
 */
export interface ReportOrgAuditEventRequest {
  type: 'export';
  surface: string;
  range: Record<string, unknown>;
}

export interface ReportOrgAuditEventResponse {
  ok: true;
  event: TenantAuditEventSummary;
}
